/**
 * WorldShopy - Global Wishlist / Favorites Manager
 */
(function () {
  'use strict';

  // Helper to determine the localStorage key depending on customer session
  function getWishlistKey() {
    const customerId = window.theme && window.theme.customerId;
    return customerId ? `wishlist_customer_${customerId}` : 'wishlist_guest';
  }

  // Global Wishlist Controller
  const Wishlist = {
    getWishlist: function () {
      const key = getWishlistKey();
      try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : [];
      } catch (e) {
        console.error('Error reading wishlist from localStorage', e);
        return [];
      }
    },

    saveWishlist: function (list) {
      const key = getWishlistKey();
      try {
        localStorage.setItem(key, JSON.stringify(list));
      } catch (e) {
        console.error('Error saving wishlist to localStorage', e);
      }
    },

    has: function (handle) {
      return this.getWishlist().includes(handle);
    },

    add: function (handle) {
      const list = this.getWishlist();
      if (!list.includes(handle)) {
        list.push(handle);
        this.saveWishlist(list);
        this.updateUI();
        // Dispatch event for other listeners
        document.dispatchEvent(new CustomEvent('wishlist:add', { detail: { handle } }));
        return true;
      }
      return false;
    },

    remove: function (handle) {
      let list = this.getWishlist();
      if (list.includes(handle)) {
        list = list.filter(h => h !== handle);
        this.saveWishlist(list);
        this.updateUI();
        // Dispatch event for other listeners
        document.dispatchEvent(new CustomEvent('wishlist:remove', { detail: { handle } }));
        return true;
      }
      return false;
    },

    toggle: function (handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false; // Removed
      } else {
        this.add(handle);
        return true; // Added
      }
    },

    updateUI: function () {
      const list = this.getWishlist();
      const count = list.length;

      // Update Desktop Header Badge
      const desktopBadge = document.getElementById('wishlist-count-badge');
      if (desktopBadge) {
        desktopBadge.textContent = count;
        desktopBadge.style.display = count > 0 ? 'flex' : 'none';
      }

      // Update Mobile Drawer Badge
      const mobileBadges = document.querySelectorAll('.ph__drawer-wishlist-count');
      mobileBadges.forEach(badge => {
        badge.textContent = count;
      });

      // Update all Heart Buttons on current page
      const heartButtons = document.querySelectorAll('.nat-product-card__wishlist, .col-prod-card__wishlist, [onclick^="toggleWishlist"]');
      heartButtons.forEach(button => {
        // Find product card container
        const card = button.closest('[data-handle]') || button.closest('.nat-product-card') || button.closest('.col-prod-card');
        if (!card) return;

        // Try getting handle from data-handle or fall back to parsing anchor href
        let handle = card.getAttribute('data-handle');
        if (!handle) {
          const link = card.querySelector('a');
          if (link) {
            const href = link.getAttribute('href');
            if (href) {
              handle = href.split('/').pop().split('?')[0];
            }
          }
        }

        if (!handle) return;

        const isFavorited = this.has(handle);
        const svg = button.querySelector('svg');

        if (isFavorited) {
          button.classList.add('active');
          if (svg) svg.setAttribute('fill', 'currentColor');
        } else {
          button.classList.remove('active');
          if (svg) svg.setAttribute('fill', 'none');
        }
      });
    }
  };

  // Expose manager globally
  window.Wishlist = Wishlist;

  // Redefine global toggleWishlist function to integrate with our storage backend
  window.toggleWishlist = function (button) {
    const card = button.closest('[data-handle]') || button.closest('.nat-product-card') || button.closest('.col-prod-card');
    if (!card) return;

    let handle = card.getAttribute('data-handle');
    if (!handle) {
      const link = card.querySelector('a');
      if (link) {
        const href = link.getAttribute('href');
        if (href) {
          handle = href.split('/').pop().split('?')[0];
        }
      }
    }

    if (!handle) return;

    // Toggle item in storage and get new state
    const isAct = Wishlist.toggle(handle);

    // Sync button UI instantly
    const svg = button.querySelector('svg');
    if (isAct) {
      button.classList.add('active');
      if (svg) svg.setAttribute('fill', 'currentColor');
    } else {
      button.classList.remove('active');
      if (svg) svg.setAttribute('fill', 'none');
    }
  };

  // Run initial UI Sync when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    // Client-side rendering fallback for /pages/favorites if it 404s
    if (window.location.pathname.toLowerCase() === '/pages/favorites') {
      if (!document.getElementById('shopify-section-main-favorites')) {
        const main = document.getElementById('MainContent');
        if (main) {
          main.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 400px; font-family: sans-serif;">
              <div style="width: 45px; height: 45px; border: 3px solid rgba(35, 74, 32, 0.1); border-top: 3px solid #234A20; border-radius: 50%; animation: wishlist-spin 1s linear infinite;"></div>
              <p style="margin-top: 20px; color: #555; font-size: 0.95rem; font-weight: 500; letter-spacing: 0.02em;">Loading your favorites...</p>
              <style>
                @keyframes wishlist-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
              </style>
            </div>
          `;
          
          fetch('/?section_id=main-favorites')
            .then(res => {
              if (!res.ok) throw new Error('Section render request failed');
              return res.text();
            })
            .then(html => {
              main.innerHTML = html;
              document.title = 'My Favorites – WorldShopy';
              
              // Find and execute script tags in the loaded HTML
              const scripts = main.querySelectorAll('script');
              scripts.forEach(oldScript => {
                const newScript = document.createElement('script');
                Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
                newScript.textContent = oldScript.textContent;
                oldScript.parentNode.replaceChild(newScript, oldScript);
              });
              
              // Run updateUI on the newly injected DOM elements
              Wishlist.updateUI();
            })
            .catch(err => {
              console.error('Error rendering favorites page client-side:', err);
              main.innerHTML = `
                <div style="text-align: center; padding: 80px 20px; font-family: sans-serif;">
                  <h2 style="color: #c93b3b; font-size: 1.8rem; margin-bottom: 15px;">Failed to Load Favorites</h2>
                  <p style="color: #666; margin-bottom: 25px;">Could not load the favorites page content. Please check your connection or reload.</p>
                  <a href="/" style="display: inline-block; padding: 12px 30px; background: #234A20; color: #fff; text-decoration: none; border-radius: 4px; font-weight: 600; transition: background 0.3s ease;">Go to Homepage</a>
                </div>
              `;
            });
        }
      }
    }
    Wishlist.updateUI();
  });

  if (document.readyState !== 'loading') {
    Wishlist.updateUI();
  }

})();
